import React, { useMemo, useState, useEffect, useRef } from "react";
import {
  CRow,
  CCol,
  CListGroup,
  CListGroupItem,
  CFormInput,
  CButton,
  CCard,
  CCardBody,
  CBadge,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import {
  cilSend,
  cilClock,
  cilCheck,
  cilCheckAlt,
  cilChatBubble,
} from "@coreui/icons";
import AppLoader from "../../components/seller/AppLoader";

const initialConversations = [
  { id: 1, buyer: "Buyer A", product: "Aluminium Scrap", unread: 2 },
  { id: 2, buyer: "Buyer B", product: "Copper Scrap", unread: 0 },
  { id: 3, buyer: "Buyer C", product: "Iron Scrap", unread: 5 },
  { id: 4, buyer: "Buyer D", product: "Steel Scrap", unread: 0 },
];

const initialMessages = [
  {
    sender: "buyer",
    text: "Hello, is this still available?",
    timestamp: "10:15 AM",
    date: "Today",
    status: "seen",
  },
  {
    sender: "seller",
    text: "Yes, it’s available.",
    timestamp: "10:16 AM",
    date: "Today",
    status: "seen",
  },
  {
    sender: "seller",
    text: "How much do you want?",
    timestamp: "10:25 AM",
    date: "Today",
    status: "delivered",
  },
];

const SellerChatPage = () => {
  const [conversations, setConversations] = useState(initialConversations);
  const [activeChat, setActiveChat] = useState(null);
  const [search, setSearch] = useState("");
  const [messages, setMessages] = useState(initialMessages);
  const [newMessage, setNewMessage] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const avatarColors = [
    "linear-gradient(135deg,#fb7185,#f97316)",
    "linear-gradient(135deg,#60a5fa,#7c3aed)",
    "linear-gradient(135deg,#34d399,#06b6d4)",
    "linear-gradient(135deg,#f59e0b,#ef4444)",
  ];

  useEffect(() => {
    if (activeChat) {
      messagesEndRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
  }, [messages, activeChat]);

  const filteredConversations = useMemo(
    () =>
      conversations.filter((c) =>
        c.buyer.toLowerCase().includes(search.toLowerCase())
      ),
    [conversations, search]
  );

  const groupedMessages = useMemo(() => {
    return messages.reduce((acc, msg) => {
      acc[msg.date] = acc[msg.date] ? [...acc[msg.date], msg] : [msg];
      return acc;
    }, {});
  }, [messages]);

  const handleSelectChat = (chat) => {
    setActiveChat(chat);
    setConversations((prev) =>
      prev.map((c) => (c.id === chat.id ? { ...c, unread: 0 } : c))
    );
    if (window.innerWidth < 992) {
      setSidebarOpen(false);
    }
  };

  const handleSend = () => {
    if (!newMessage.trim() || !activeChat) return;
    const msg = {
      sender: "seller",
      text: newMessage.trim(),
      timestamp: new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
      date: "Today",
      status: "sent",
    };
    setMessages((prev) => [...prev, msg]);
    setNewMessage("");
    setConversations((prev) =>
      prev.map((c) => (c.id === activeChat.id ? { ...c, unread: 0 } : c))
    );
  };

  if (loading) return <AppLoader />;

  return (
    <div className="seller-chat-root">
      <CRow className="h-100 flex-nowrap">
        {/* Sidebar */}
        <CCol
          md={4}
          lg={3}
          className={`sidebar-col ${sidebarOpen ? "open" : "closed"}`}
        >
          <div className="sidebar-top px-3">
            <h6 className="mb-2">Conversations</h6>
            <CFormInput
              size="sm"
              placeholder="Search buyer..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="search-input w-100"
              aria-label="Search conversations"
            />
          </div>

          <div className="sidebar-list">
            <CListGroup flush>
              {filteredConversations.map((chat) => (
                <CListGroupItem
                  key={chat.id}
                  active={activeChat?.id === chat.id}
                  onClick={() => handleSelectChat(chat)}
                  className="conversation-item d-flex align-items-center"
                >
                  <div
                    className="avatar me-3"
                    style={{
                      background:
                        avatarColors[(chat.id - 1) % avatarColors.length],
                      color: "#fff",
                    }}
                  >
                    {chat.buyer.charAt(0)}
                  </div>

                  <div className="flex-grow-1">
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="fw-semibold conversation-name">
                        {chat.buyer}
                      </div>
                      <small className="text-muted">10:45</small>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mt-1">
                      <small className="text-muted text-truncate last-msg">
                        {initialMessages[initialMessages.length - 1]?.text ||
                          chat.product}
                      </small>
                      {chat.unread > 0 && (
                        <CBadge
                          color="primary"
                          shape="rounded-pill"
                          className="unread-badge"
                        >
                          {chat.unread}
                        </CBadge>
                      )}
                    </div>
                  </div>
                </CListGroupItem>
              ))}
            </CListGroup>
          </div>

          <div className="sidebar-footer px-3 py-2">
            <small className="text-muted">
              Online buyers: {conversations.length}
            </small>
          </div>
        </CCol>

        {/* Chat */}
        <CCol md={8} lg={9} className="chat-col">
          <CCard className="chat-card">
            <div className="chat-header d-flex align-items-center px-3">
              {activeChat ? (
                <>
                  <div
                    className="avatar me-3"
                    style={{
                      background:
                        avatarColors[(activeChat.id - 1) % avatarColors.length],
                      color: "#fff",
                    }}
                  >
                    {activeChat.buyer.charAt(0)}
                  </div>
                  <div>
                    <div className="fw-bold">{activeChat.buyer}</div>
                    <small className="text-muted">{activeChat.product}</small>
                  </div>
                </>
              ) : (
                <div className="empty-header d-flex align-items-center gap-2">
                  <div className="placeholder-icon">
                    <CIcon icon={cilChatBubble} size={24} />
                  </div>
                  <div>
                    <div className="fw-bold">No conversation selected</div>
                    <small className="text-muted">
                      Select a buyer to start chatting
                    </small>
                  </div>
                </div>
              )}
            </div>

            <CCardBody className="chat-body p-0">
              {activeChat ? (
                <div className="messages-wrap p-3">
                  {Object.keys(groupedMessages).map((date) => (
                    <div key={date} className="message-group">
                      <div className="date-chip-wrapper">
                        <span className="date-chip">{date}</span>
                      </div>

                      {groupedMessages[date].map((msg, idx) => (
                        <div
                          key={idx}
                          className={`message-row ${
                            msg.sender === "seller" ? "from-me" : "from-them"
                          }`}
                        >
                          <div
                            className={`message-bubble ${
                              msg.sender === "seller" ? "me" : "them"
                            }`}
                          >
                            <div className="message-text">{msg.text}</div>
                            <div className="message-meta">
                              <span className="time">{msg.timestamp}</span>
                              {msg.sender === "seller" && (
                                <span className="status-icon">
                                  {msg.status === "sent" && (
                                    <CIcon icon={cilClock} />
                                  )}
                                  {msg.status === "delivered" && (
                                    <CIcon icon={cilCheckAlt} />
                                  )}
                                  {msg.status === "seen" && (
                                    <CIcon icon={cilCheck} />
                                  )}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              ) : (
                <div className="empty-state d-flex flex-column justify-content-center align-items-center h-100 p-4">
                  <div className="bubble-icon mb-3">
                    <CIcon icon={cilChatBubble} size={32} />
                  </div>
                  <h5>Select a conversation</h5>
                  <p className="text-muted">
                    You can search buyers and start a conversation from the left
                    sidebar.
                  </p>
                </div>
              )}
            </CCardBody>

            <div className="chat-input d-flex align-items-center px-3 py-2 border-top bg-white">
              <CFormInput
                placeholder={
                  activeChat
                    ? "Type your message..."
                    : "Select a conversation to start"
                }
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                disabled={!activeChat}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSend();
                }}
              />

              <CButton
                color="primary"
                className="ms-2"
                onClick={handleSend}
                disabled={!activeChat || !newMessage.trim()}
              >
                <CIcon icon={cilSend} />
              </CButton>
            </div>
          </CCard>
        </CCol>
      </CRow>

      <style>{`
        .seller-chat-root {
          height: 80vh;
          display: flex;
          flex-direction: column;
          font-family: Inter, system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial;
        }

        .seller-chat-root .row {
          flex: 1;
          height: 100%;
        }

        /* Sidebar */
        .sidebar-col {
          padding: 0;
          border-right: 1px solid rgba(15,23,42,0.06);
          display: flex;
          flex-direction: column;
          background: linear-gradient(180deg,#ffffff,#fbfdff);
        }
        .sidebar-top {
          padding: 16px;
          border-bottom: 1px solid rgba(15,23,42,0.04);
          display: flex;
          flex-direction: column;
        }
        .sidebar-top h6 {
          font-weight: 600;
        }
        .sidebar-top .form-control {
          border-radius: 999px;
          padding-left: 14px;
        }

        .sidebar-top .search-input { border-radius: 6px !important; padding-left: 12px; padding-top: 8px; padding-bottom: 8px; }
        .sidebar-list { overflow:auto; flex: 1 1 auto; padding: 10px; }
        .sidebar-footer { border-top: 1px solid rgba(15,23,42,0.04); padding:10px; }
        .conversation-item { border: none; cursor: pointer; padding: 10px 12px; border-radius: 12px; transition: background 0.18s ease, transform 0.12s ease; }

        /* Active state fix */
        .conversation-item.active,
        .conversation-item[aria-pressed="true"] {
          background: linear-gradient(90deg, rgba(247,250,253,0.9), rgba(240,244,255,0.95));
          transform: translateX(4px);
          box-shadow: 0 6px 18px rgba(2,6,23,0.04);
          color: inherit !important; /* prevent white text */
        }
        .conversation-item.active .conversation-name,
        .conversation-item.active .last-msg,
        .conversation-item.active small {
          color: #0f172a !important; /* keep normal dark text */
        }

        .conversation-item:hover { background: rgba(248,249,250,0.9); }
        .avatar { width: 48px; height: 48px; border-radius: 50%; display: inline-grid; place-items:center; font-weight:700; color: #fff; box-shadow: 0 6px 18px rgba(2,6,23,0.04); }
        .conversation-name { font-size: 0.95rem; }
        .last-msg { max-width: 180px; display: inline-block; color: #6b7280; }
        .unread-badge { font-size: 0.7rem; padding: 4px 8px; background: #2563eb; color: #fff; }

        /* Chat area */
        .chat-col { padding-left: 0; padding-right: 0; display:flex; flex-direction:column; }
        .chat-card { border: none; box-shadow: 0 12px 40px rgba(2,6,23,0.06); display: flex; flex-direction: column; border-radius: 12px; overflow: hidden; flex:1; }
        .chat-header { min-height: 72px; border-bottom: 1px solid rgba(15,23,42,0.04); display:flex; align-items:center; gap:12px; padding: 12px 16px; background: linear-gradient(90deg,#ffffff,#fbfdff); }
        .chat-body { overflow:auto; flex: 1 1 auto; background: linear-gradient(180deg,#f8fafc 0%, #f1f5f9 100%); padding: 12px; }
        .messages-wrap { padding-bottom: 20px; }
        .date-chip-wrapper { text-align: center; margin: 12px 0; }
        .date-chip { display:inline-block; background: rgba(233,236,239,0.95); padding:6px 14px; border-radius:6px; color: #6c757d; font-weight:600; font-size:0.78rem; }
        .message-row { margin-bottom: 14px; display: flex; }
        .message-row.from-me { justify-content: flex-end; }
        .message-row.from-them { justify-content: flex-start; }
        .message-bubble { max-width: 78%; padding: 12px 14px; border-radius: 14px; box-shadow: 0 10px 30px rgba(2,6,23,0.06); line-height:1.3; }
        .message-bubble.me { background: linear-gradient(180deg,#fff6ea 0%, #fff1dd 100%); color: #0f172a; border-bottom-right-radius: 6px; }
        .message-bubble.them { background: #fff; color: #0f172a; border-bottom-left-radius: 6px; }
        .message-text { white-space: pre-wrap; }
        .message-meta { display:flex; gap: 8px; align-items:center; margin-top:8px; font-size: 0.72rem; color: #6b7280; }

        /* Input */
        .chat-input { gap: 8px; display:flex; align-items:center; padding: 12px; background: linear-gradient(180deg,#fff,#fbfdff); }
        .chat-input .form-control { border-radius: 12px; padding: 12px 16px; }
        .chat-input .btn { border-radius: 999px; padding: 8px 12px; }

        /* Sidebar collapse for small screens */
        .sidebar-col.closed { display: none; }

        /* Responsive */
        @media (max-width: 992px) {
          .sidebar-col { position: relative; z-index: 10; }
        }
        @media (max-width: 576px) {
          .seller-chat-root { padding: 8px; }
          .conversation-item { padding: 8px 10px; }
        }
      `}</style>
    </div>
  );
};

export default SellerChatPage;
